import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms & Conditions</h1>
        <p className="text-sm text-gray-500 mb-10">Effective Date: April 2, 2026</p>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              Welcome to iHireFast. These Terms & Conditions ("Terms") govern your access to and
              use of the iHireFast platform, including our website, dashboard, APIs, and related
              services (collectively, the "Service"). By creating an account or using the Service,
              you agree to be bound by these Terms. If you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Service Description</h2>
            <p className="text-gray-600 leading-relaxed">
              iHireFast is a recruitment automation platform that helps businesses streamline their
              hiring process. The Service includes automated SMS communication with applicants,
              applicant tracking, interview scheduling, resume management, customizable application
              forms, and integration with third-party services such as Indeed, Calendly, and SMS
              providers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Account Registration</h2>
            <p className="text-gray-600 leading-relaxed">
              To use the Service, you must create an account and provide accurate, complete
              information. You are responsible for maintaining the confidentiality of your account
              credentials and for all activities that occur under your account. You must notify us
              immediately of any unauthorized access or use of your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. User Responsibilities</h2>
            <p className="text-gray-600 leading-relaxed mb-3">By using the Service, you agree to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Use the Service only for lawful recruitment and hiring purposes</li>
              <li>Comply with all applicable laws, including employment, anti-discrimination, and data protection regulations</li>
              <li>Obtain proper consent before sending SMS messages to applicants</li>
              <li>Not use the Service to send spam, unsolicited messages, or misleading content</li>
              <li>Not attempt to reverse-engineer, decompile, or disassemble the Service</li>
              <li>Not interfere with or disrupt the integrity or performance of the Service</li>
              <li>Ensure that all data you upload or transmit is accurate and you have the right to use it</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Usage</h2>
            <p className="text-gray-600 leading-relaxed">
              You retain ownership of all data you upload to the Service, including applicant
              information, resumes, and communications. We process your data solely to provide
              and improve the Service. We do not sell your data to third parties. For more
              information on how we handle data, please see our{' '}
              <Link to="/privacy" className="text-primary-600 hover:text-primary-700 underline">
                Privacy Policy
              </Link>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Subscription & Billing</h2>
            <p className="text-gray-600 leading-relaxed">
              Certain features of the Service require a paid subscription. By subscribing, you
              agree to pay the applicable fees. Subscriptions renew automatically unless cancelled
              before the renewal date. We reserve the right to change pricing with 30 days' notice.
              Refunds are handled on a case-by-case basis at our discretion.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Third-Party Integrations</h2>
            <p className="text-gray-600 leading-relaxed">
              The Service integrates with third-party platforms including Indeed, Calendly, and SMS
              providers. Your use of these integrations is subject to the respective third-party
              terms of service. We are not responsible for the availability, accuracy, or
              performance of third-party services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed">
              The Service, including its design, features, code, and documentation, is the
              intellectual property of iHireFast and is protected by copyright, trademark, and
              other laws. You are granted a limited, non-exclusive, non-transferable license to
              use the Service for its intended purpose during your subscription.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed">
              To the maximum extent permitted by law, iHireFast shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages, including loss
              of profits, data, or business opportunities, arising out of your use of the Service.
              Our total liability shall not exceed the amount you paid for the Service in the
              twelve months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Disclaimer of Warranties</h2>
            <p className="text-gray-600 leading-relaxed">
              The Service is provided "as is" and "as available" without warranties of any kind,
              whether express or implied, including but not limited to warranties of
              merchantability, fitness for a particular purpose, and non-infringement. We do not
              guarantee that the Service will be uninterrupted, error-free, or secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Termination</h2>
            <p className="text-gray-600 leading-relaxed">
              Either party may terminate the agreement at any time. You may cancel your account
              through the dashboard settings. We may suspend or terminate your access if you
              violate these Terms or if we reasonably believe your use poses a risk to the Service
              or other users. Upon termination, your right to use the Service ceases immediately.
              We will retain your data for 30 days after termination, after which it may be
              permanently deleted.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Modifications to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update these Terms from time to time. We will notify you of material changes
              by email or through the Service. Your continued use of the Service after changes
              take effect constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">13. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the
              United States. Any disputes arising from these Terms or the Service shall be resolved
              through binding arbitration in accordance with applicable rules.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">14. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have questions about these Terms, please contact us at{' '}
              <a href="mailto:support@ihirefast.com" className="text-primary-600 hover:text-primary-700 underline">
                support@ihirefast.com
              </a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
